import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Chip,
  Divider,
} from "@mui/material";
import { History as HistoryIcon } from "@mui/icons-material";
import { Assessment } from "../../../redux/reducers/assessmentReducer";

interface AssessmentHistoryProps {
  currentAssessmentId: string;
  assessmentHistory: Assessment[];
  historyLoading: boolean;
}

const AssessmentHistory: React.FC<AssessmentHistoryProps> = ({
  currentAssessmentId,
  assessmentHistory,
  historyLoading,
}) => {
  const navigate = useNavigate();

  if (!assessmentHistory || assessmentHistory.length === 0) {
    return null;
  }

  return (
    <Paper sx={{ p: 3 }}>
      <Box display="flex" alignItems="center" gap={1} mb={2}>
        <HistoryIcon color="primary" />
        <Typography variant="h6">Assessment History</Typography>
      </Box>
      <Divider sx={{ mb: 3 }} />

      {historyLoading ? (
        <Box display="flex" justifyContent="center" p={3}>
          <CircularProgress size={24} />
        </Box>
      ) : (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Version</TableCell>
                <TableCell>Assessment ID</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Environment</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {[...assessmentHistory]
                .sort(
                  (a, b) =>
                    new Date(b.creation_time).getTime() -
                    new Date(a.creation_time).getTime()
                )
                .map((assessment, index) => (
                  <TableRow
                    key={assessment.id}
                    sx={{
                      bgcolor:
                        assessment.id === currentAssessmentId
                          ? "action.selected"
                          : "inherit",
                    }}
                  >
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="body2">
                          v{assessmentHistory.length - index}
                        </Typography>
                        {assessment.id === currentAssessmentId && (
                          <Chip label="Current" size="small" color="primary" />
                        )}
                        {index === 0 && assessment.is_active !== false && (
                          <Chip label="Active" size="small" color="success" />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{ fontFamily: "monospace" }}
                      >
                        {assessment.id}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={assessment.generation_status}
                        size="small"
                        color={
                          assessment.generation_status === "done"
                            ? "success"
                            : assessment.generation_status === "pending"
                            ? "warning"
                            : "default"
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={assessment.environment}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {new Date(assessment.creation_time).toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {assessment.id !== currentAssessmentId && (
                        <Button
                          size="small"
                          onClick={() =>
                            navigate(`/dashboard/assessments/${assessment.organization_id}/${assessment.id}`)
                          }
                        >
                          View
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
};

export default AssessmentHistory;
